import { useCallback, useEffect, useRef, useState } from 'react';
import { PaperAirplaneIcon } from '@heroicons/react/24/solid';
import { MicrophoneIcon } from '@heroicons/react/24/outline';
import useVoiceInput from '../../hooks/useVoiceInput';
import MicButton from '../voice/MicButton';
import { MODES } from '../../hooks/useChatMode';

/**
 * The composer for Type mode, and the launch pad for the two hands-free modes.
 *
 * Type mode keeps a dictation mic: record one clip, transcribe it into the
 * draft, and you still tap send. That's dictation, not a voice mode — nothing
 * is ever sent without an explicit tap.
 */
export default function ChatInput({
  onSend,
  disabled,
  mode = 'type',
  onStartVoice,
  onVoiceGesture,
}) {
  const [input, setInput] = useState('');
  const textareaRef = useRef(null);
  const inputRef = useRef('');
  // Lets submit() stop the recorder without depending on the voice object,
  // which is created further down (after submit).
  const voiceStopRef = useRef(null);

  useEffect(() => {
    inputRef.current = input;
  }, [input]);

  const submit = useCallback(
    (explicit) => {
      const text = (explicit ?? inputRef.current).trim();
      if (!text || disabled) return;
      voiceStopRef.current?.();
      onSend(text);
      setInput('');
      inputRef.current = '';
    },
    [disabled, onSend]
  );

  // Each finished clip returns one clean chunk — append it to the draft.
  const handleTranscript = useCallback((text) => {
    setInput((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text));
  }, []);

  const voice = useVoiceInput({ onTranscript: handleTranscript });
  const { stop: stopVoice, start: startVoice, reset: resetVoice, cancel: cancelVoice } = voice;

  useEffect(() => {
    voiceStopRef.current = () => {
      stopVoice();
      resetVoice();
    };
  }, [stopVoice, resetVoice]);

  const autoGrow = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(() => {
    autoGrow(textareaRef.current);
  }, [input]);

  // Leaving Type mode must not leave the mic live.
  useEffect(() => {
    if (mode !== 'type') cancelVoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const handleSubmit = (e) => {
    e.preventDefault();
    submit();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  // Both voice modes are the same full-screen surface, launched from here.
  if (MODES[mode]?.handsFree) {
    const walkie = mode === 'walkie';
    return (
      <div className="p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800">
        <button
          type="button"
          onClick={onStartVoice}
          disabled={disabled}
          className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-white bg-gradient-to-r from-indigo-600 to-violet-600 shadow-sm hover:shadow-md transition-all disabled:opacity-50"
        >
          <MicrophoneIcon className="w-5 h-5" />
          {walkie ? 'Start walkie-talkie' : 'Start hands-free conversation'}
        </button>
        <p className="mt-2 text-xs text-center text-gray-500 dark:text-gray-400">
          {walkie
            ? 'Talk as long as you like — it only answers when you say “over over” or tap Send.'
            : 'Just talk — it listens, answers aloud, then hands the mic back. Pause ~3s to end your turn.'}
        </p>
      </div>
    );
  }

  const placeholder = voice.transcribing
    ? 'Transcribing…'
    : voice.listening
      ? 'Recording — tap the mic to finish'
      : 'Type your message, or tap the mic';

  return (
    <form
      onSubmit={handleSubmit}
      className="p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800"
    >
      {voice.listening && (
        <p className="mb-2 px-1 text-xs text-center text-rose-600 dark:text-rose-400">
          Recording — tap the mic to finish, then send
        </p>
      )}
      {voice.transcribing && (
        <p className="mb-2 px-1 text-xs text-center text-gray-500 dark:text-gray-400">
          Transcribing…
        </p>
      )}

      <div className="flex gap-2 items-end">
        <MicButton
          size="inline"
          supported={voice.supported}
          listening={voice.listening}
          onStart={() => {
            onVoiceGesture?.();
            startVoice();
          }}
          onStop={stopVoice}
          disabled={disabled || voice.transcribing}
          className="!rounded-xl shrink-0"
        />
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={1}
          className="flex-1 px-4 py-2 max-h-32 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-transparent resize-none transition-colors"
        />
        <button
          type="submit"
          disabled={disabled || !input.trim()}
          className="p-2 h-11 w-11 flex items-center justify-center shrink-0 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PaperAirplaneIcon className="w-5 h-5" />
        </button>
      </div>
    </form>
  );
}
